import { Theme } from '../types/theme';

// Base typography and spacing shared across themes
const baseTypography = {
  fontFamily: {
    primary: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: 'Fira Code, Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace'
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem'
  },
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75
  }
};

const baseSpacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '2.5rem',
  '3xl': '3rem',
  '4xl': '4rem'
};

const baseBorderRadius = {
  none: '0',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  full: '9999px'
};

const baseShadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
};

const baseBreakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
};

// Default Theme - Original clean white design
export const defaultTheme: Theme = {
  id: 'default',
  name: 'Default',
  description: 'Original clean design with white background',
  colors: {
    primary: '#3880ff',
    primaryLight: '#5598ff',
    primaryDark: '#2968cc',
    secondary: '#6c757d',
    secondaryLight: '#868e96',
    secondaryDark: '#495057',
    success: '#2dd36f',
    warning: '#ffc409',
    error: '#eb445a',
    info: '#3dc2ff',
    background: '#ffffff',
    surface: '#ffffff',
    surfaceVariant: '#f8f9fa',
    onPrimary: '#ffffff',
    onSecondary: '#ffffff',
    onBackground: '#000000',
    onSurface: '#000000',
    onSurfaceVariant: '#6c757d',
    border: '#dee2e6',
    divider: '#e9ecef',
    hover: '#f8f9fa',
    focus: '#e3f2fd',
    active: '#bbdefb',
    disabled: '#e9ecef'
  },
  typography: baseTypography,
  spacing: baseSpacing,
  borderRadius: baseBorderRadius,
  shadows: baseShadows,
  breakpoints: baseBreakpoints,
  isDark: false,
  isCustom: false
};

// Professional Theme - Clean, corporate, trustworthy
export const professionalTheme: Theme = {
  id: 'professional',
  name: 'Professional',
  description: 'Clean and corporate design perfect for business coaching',
  colors: {
    primary: '#2563eb',
    primaryLight: '#3b82f6',
    primaryDark: '#1d4ed8',
    secondary: '#64748b',
    secondaryLight: '#94a3b8',
    secondaryDark: '#475569',
    success: '#059669',
    warning: '#d97706',
    error: '#dc2626',
    info: '#0284c7',
    background: '#e6f3ff',
    surface: '#cce7ff',
    surfaceVariant: '#b3dbff',
    onPrimary: '#ffffff',
    onSecondary: '#ffffff',
    onBackground: '#0f172a',
    onSurface: '#1e293b',
    onSurfaceVariant: '#475569',
    border: '#e2e8f0',
    divider: '#f1f5f9',
    hover: '#f8fafc',
    focus: '#dbeafe',
    active: '#bfdbfe',
    disabled: '#f1f5f9'
  },
  typography: baseTypography,
  spacing: baseSpacing,
  borderRadius: baseBorderRadius,
  shadows: baseShadows,
  breakpoints: baseBreakpoints,
  isDark: false,
  isCustom: false
};

// Modern Theme - Sleek, contemporary, tech-forward
export const modernTheme: Theme = {
  id: 'modern',
  name: 'Modern',
  description: 'Sleek and contemporary design with bold accents',
  colors: {
    primary: '#8b5cf6',
    primaryLight: '#a78bfa',
    primaryDark: '#7c3aed',
    secondary: '#06b6d4',
    secondaryLight: '#22d3ee',
    secondaryDark: '#0891b2',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#06b6d4',
    background: '#f3e8ff',
    surface: '#e9d5ff',
    surfaceVariant: '#ddd6fe',
    onPrimary: '#ffffff',
    onSecondary: '#ffffff',
    onBackground: '#1e293b',
    onSurface: '#334155',
    onSurfaceVariant: '#475569',
    border: '#8b5cf6',
    divider: '#cbd5e1',
    hover: '#e2e8f0',
    focus: '#ddd6fe',
    active: '#c4b5fd',
    disabled: '#e2e8f0'
  },
  typography: {
    ...baseTypography,
    fontFamily: {
      primary: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      secondary: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: baseTypography.fontFamily.mono
    }
  },
  spacing: baseSpacing,
  borderRadius: {
    ...baseBorderRadius,
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem'
  },
  shadows: baseShadows,
  breakpoints: baseBreakpoints,
  isDark: false,
  isCustom: false
};

// Accessible Theme - High contrast, clear readability
export const accessibleTheme: Theme = {
  id: 'accessible',
  name: 'Accessible',
  description: 'High contrast design optimized for accessibility',
  colors: {
    primary: '#000000',
    primaryLight: '#333333',
    primaryDark: '#000000',
    secondary: '#333333',
    secondaryLight: '#666666',
    secondaryDark: '#000000',
    success: '#006600',
    warning: '#cc6600',
    error: '#cc0000',
    info: '#0066cc',
    background: '#f5f5f5',
    surface: '#eeeeee',
    surfaceVariant: '#e0e0e0',
    onPrimary: '#ffffff',
    onSecondary: '#ffffff',
    onBackground: '#000000',
    onSurface: '#000000',
    onSurfaceVariant: '#000000',
    border: '#000000',
    divider: '#cccccc',
    hover: '#f0f0f0',
    focus: '#ffff00',
    active: '#ffcc00',
    disabled: '#cccccc'
  },
  typography: {
    ...baseTypography,
    fontWeight: {
      light: 400,
      normal: 500,
      medium: 600,
      semibold: 700,
      bold: 800
    }
  },
  spacing: {
    ...baseSpacing,
    sm: '0.75rem',
    md: '1.25rem',
    lg: '2rem'
  },
  borderRadius: {
    ...baseBorderRadius,
    sm: '0.125rem',
    md: '0.25rem'
  },
  shadows: {
    sm: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    md: '0 4px 8px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.12)',
    lg: '0 10px 20px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 30px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
  },
  breakpoints: baseBreakpoints,
  isDark: false,
  isCustom: false
};

// Minimal Theme - Clean, spacious, distraction-free
export const minimalTheme: Theme = {
  id: 'minimal',
  name: 'Minimal',
  description: 'Clean and distraction-free design for focused work',
  colors: {
    primary: '#000000',
    primaryLight: '#404040',
    primaryDark: '#000000',
    secondary: '#737373',
    secondaryLight: '#a3a3a3',
    secondaryDark: '#525252',
    success: '#166534',
    warning: '#a16207',
    error: '#b91c1c',
    info: '#0369a1',
    background: '#ffffff',
    surface: '#ffffff',
    surfaceVariant: '#fafafa',
    onPrimary: '#ffffff',
    onSecondary: '#ffffff',
    onBackground: '#000000',
    onSurface: '#171717',
    onSurfaceVariant: '#404040',
    border: '#e5e5e5',
    divider: '#f5f5f5',
    hover: '#fafafa',
    focus: '#f5f5f5',
    active: '#e5e5e5',
    disabled: '#f5f5f5'
  },
  typography: {
    ...baseTypography,
    fontFamily: {
      primary: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      secondary: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: baseTypography.fontFamily.mono
    }
  },
  spacing: {
    ...baseSpacing,
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem'
  },
  borderRadius: {
    none: '0',
    sm: '0',
    md: '0',
    lg: '0',
    xl: '0',
    full: '0'
  },
  shadows: {
    sm: 'none',
    md: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    xl: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  breakpoints: baseBreakpoints,
  isDark: false,
  isCustom: false
};

// Vibrant Theme - Energetic, colorful, inspiring
export const vibrantTheme: Theme = {
  id: 'vibrant',
  name: 'Vibrant',
  description: 'Energetic and colorful design to inspire creativity',
  colors: {
    primary: '#ff0080',
    primaryLight: '#ff4da6',
    primaryDark: '#cc0066',
    secondary: '#8000ff',
    secondaryLight: '#a64dff',
    secondaryDark: '#6600cc',
    success: '#00ff80',
    warning: '#ff8000',
    error: '#ff0040',
    info: '#0080ff',
    background: '#fffaff',
    surface: '#fff0ff',
    surfaceVariant: '#ffe6ff',
    onPrimary: '#ffffff',
    onSecondary: '#ffffff',
    onBackground: '#000000',
    onSurface: '#000000',
    onSurfaceVariant: '#000000',
    border: '#ff0080',
    divider: '#ff80c0',
    hover: '#ffe6ff',
    focus: '#ffccf0',
    active: '#ffb3e6',
    disabled: '#f0f0f0'
  },
  typography: {
    ...baseTypography,
    fontFamily: {
      primary: 'Nunito, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      secondary: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: baseTypography.fontFamily.mono
    }
  },
  spacing: baseSpacing,
  borderRadius: {
    ...baseBorderRadius,
    md: '0.5rem',
    lg: '1rem',
    xl: '1.5rem'
  },
  shadows: {
    sm: '0 2px 4px 0 rgba(236, 72, 153, 0.1)',
    md: '0 4px 6px -1px rgba(236, 72, 153, 0.1), 0 2px 4px -1px rgba(236, 72, 153, 0.06)',
    lg: '0 10px 15px -3px rgba(236, 72, 153, 0.1), 0 4px 6px -2px rgba(236, 72, 153, 0.05)',
    xl: '0 20px 25px -5px rgba(236, 72, 153, 0.1), 0 10px 10px -5px rgba(236, 72, 153, 0.04)',
    '2xl': '0 25px 50px -12px rgba(236, 72, 153, 0.25)'
  },
  breakpoints: baseBreakpoints,
  isDark: false,
  isCustom: false
};

// Dark variants
export const professionalDarkTheme: Theme = {
  ...professionalTheme,
  id: 'professional-dark',
  name: 'Professional Dark',
  description: 'Dark variant of the professional theme',
  colors: {
    ...professionalTheme.colors,
    primary: '#3b82f6',
    primaryLight: '#60a5fa',
    primaryDark: '#2563eb',
    background: '#0f172a',
    surface: '#1e293b',
    surfaceVariant: '#334155',
    onBackground: '#f8fafc',
    onSurface: '#e2e8f0',
    onSurfaceVariant: '#cbd5e1',
    border: '#475569',
    divider: '#334155',
    hover: '#334155',
    focus: '#1e40af',
    active: '#1d4ed8',
    disabled: '#374151'
  },
  isDark: true
};

export const modernDarkTheme: Theme = {
  ...modernTheme,
  id: 'modern-dark',
  name: 'Modern Dark',
  description: 'Dark variant of the modern theme',
  colors: {
    ...modernTheme.colors,
    primary: '#a78bfa',
    primaryLight: '#c4b5fd',
    primaryDark: '#8b5cf6',
    background: '#111827',
    surface: '#1f2937',
    surfaceVariant: '#374151',
    onBackground: '#f9fafb',
    onSurface: '#e5e7eb',
    onSurfaceVariant: '#d1d5db',
    border: '#4b5563',
    divider: '#374151',
    hover: '#374151',
    focus: '#581c87',
    active: '#6b21a8',
    disabled: '#4b5563'
  },
  isDark: true
};

// Export all themes
export const defaultThemes: Theme[] = [
  defaultTheme,
  professionalTheme,
  modernTheme,
  accessibleTheme,
  minimalTheme,
  vibrantTheme,
  professionalDarkTheme,
  modernDarkTheme
];

export const getThemeById = (id: string): Theme | undefined => {
  return defaultThemes.find(theme => theme.id === id);
};

export const getDefaultTheme = (): Theme => {
  return defaultTheme;
}; 